import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// 전역 레벨 참조 카운트로 스크롤 락 충돌 방지
let activeLocksCount = 0;
let savedScrollYForUnlock = 0;

const applyBodyScrollLock = () => {
  savedScrollYForUnlock = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${savedScrollYForUnlock}px`;
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';
};

const releaseBodyScrollLock = () => {
  const y = savedScrollYForUnlock;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.body.style.overflow = '';
  // 복원은 마지막 해제 시 한 번만 수행
  window.scrollTo(0, y);
};

const useModalScrollLock = (isOpen, dependencies = []) => {
  const hasAcquiredRef = useRef(false);

  useEffect(() => {
    const isAnyModalOpen = Boolean(isOpen) || dependencies.some(Boolean);

    // 락 획득
    if (isAnyModalOpen && !hasAcquiredRef.current) {
      if (activeLocksCount === 0) {
        applyBodyScrollLock();
      }
      activeLocksCount += 1;
      hasAcquiredRef.current = true;
    }

    // 락 해제
    if (!isAnyModalOpen && hasAcquiredRef.current) {
      activeLocksCount = Math.max(0, activeLocksCount - 1);
      hasAcquiredRef.current = false;
      if (activeLocksCount === 0) {
        releaseBodyScrollLock();
      }
    }

    // 언마운트/의존성 변경 시 안전 해제
    return () => {
      if (hasAcquiredRef.current) {
        activeLocksCount = Math.max(0, activeLocksCount - 1);
        hasAcquiredRef.current = false;
        if (activeLocksCount === 0) {
          releaseBodyScrollLock();
        }
      }
    };
  }, [isOpen, dependencies]);
};

const Modal = ({
  isOpen,
  onClose,
  size = 'md',
  title,
  showCloseButton = true,
  closeOnOverlayClick = true,
  children,
}) => {
  // 모달이 열려있는 동안 body 스크롤 잠금
  useModalScrollLock(isOpen);

  // 오버레이에서 눌렀을 때만 닫히도록 제어
  const isPointerDownOnOverlayRef = useRef(false);

  const handleOverlayMouseDown = (e) => {
    if (!closeOnOverlayClick) return;
    isPointerDownOnOverlayRef.current = e.target === e.currentTarget;
  };

  const handleOverlayMouseUp = (e) => {
    if (!closeOnOverlayClick) return;
    const startedOnOverlay = isPointerDownOnOverlayRef.current;
    isPointerDownOnOverlayRef.current = false;
    if (startedOnOverlay && e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const handleOverlayTouchStart = (e) => {
    if (!closeOnOverlayClick) return;
    if (e.touches && e.touches.length > 0) {
      // 터치 시작 지점이 오버레이인지 체크
      const touchTarget = e.target;
      isPointerDownOnOverlayRef.current = touchTarget === e.currentTarget;
    }
  };

  const handleOverlayTouchEnd = (e) => {
    if (!closeOnOverlayClick) return;
    const startedOnOverlay = isPointerDownOnOverlayRef.current;
    isPointerDownOnOverlayRef.current = false;
    if (startedOnOverlay) {
      onClose && onClose();
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlayClass = size === 'lg' ? 'modal-overlay-large' : 'modal-overlay';
  const contentClass = size === 'lg' ? 'modal-content-large' : 'modal-content';

  return (
    <div
      className={overlayClass}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      onTouchStart={handleOverlayTouchStart}
      onTouchEnd={handleOverlayTouchEnd}
    >
      <div className={contentClass} onClick={(e) => e.stopPropagation()}>
        {showCloseButton && (
          <div className="modal-header">
            <button className="modal-close-btn" onClick={onClose} aria-label="닫기"><X /></button>
          </div>
        )}
        {title && (
          <div className="modal-title-row">
            <h3 className="modal-title">{title}</h3>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default Modal;